(() => {
  if (window.moxLanguageLayoutReady) return;
  window.moxLanguageLayoutReady = true;
  const observed = new WeakSet();
  let frame = 0;
  let allSectionsDirty = false;
  const dirtySections = new Set();
  const sectionOf = card => card.closest('.shopify-section') || card.parentElement;
  let measuredBounds = null;
  const boundsOf = element => {
    if (!measuredBounds) return element.getBoundingClientRect();
    if (!measuredBounds.has(element)) measuredBounds.set(element, element.getBoundingClientRect());
    return measuredBounds.get(element);
  };
  const numeric = value => Number.parseFloat(value) || 0;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const paintedBounds = image => {
    const rect = boundsOf(image);
    const css = getComputedStyle(image);
    const left = numeric(css.borderLeftWidth) + numeric(css.paddingLeft);
    const right = numeric(css.borderRightWidth) + numeric(css.paddingRight);
    const top = numeric(css.borderTopWidth) + numeric(css.paddingTop);
    const bottom = numeric(css.borderBottomWidth) + numeric(css.paddingBottom);
    const width = Math.max(0, rect.width - left - right);
    const height = Math.max(0, rect.height - top - bottom);
    let actualWidth = width, actualHeight = height;
    if (css.objectFit === 'contain' && image.naturalWidth && image.naturalHeight) {
      const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
      actualWidth = image.naturalWidth * scale;
      actualHeight = image.naturalHeight * scale;
    }
    const tokens = css.objectPosition.split(' ');
    const fraction = (token, low, high) => token === low ? 0 : token === high ? 1 : token?.endsWith('%') ? numeric(token) / 100 : 0.5;
    const x = rect.left + left + (width - actualWidth) * fraction(tokens[0], 'left', 'right');
    const y = rect.top + top + (height - actualHeight) * fraction(tokens[1], 'top', 'bottom');
    return {left:x, top:y, right:x + actualWidth, bottom:y + actualHeight};
  };
  const visibleCardImage = card => {
    const cardRect = boundsOf(card);
    const gallery = card.querySelector('.card-gallery');
    const galleryRect = gallery ? boundsOf(gallery) : cardRect;
    const clip = {
      left: Math.max(cardRect.left, galleryRect.left),
      right: Math.min(cardRect.right, galleryRect.right),
      top: Math.max(cardRect.top, galleryRect.top),
      bottom: Math.min(cardRect.bottom, galleryRect.bottom),
    };
    let image = null, largestArea = 0;
    for (const candidate of card.querySelectorAll('img.product-media__image')) {
      const rect = boundsOf(candidate);
      const width = Math.max(0, Math.min(rect.right, clip.right) - Math.max(rect.left, clip.left));
      const height = Math.max(0, Math.min(rect.bottom, clip.bottom) - Math.max(rect.top, clip.top));
      const area = width * height;
      if (area > largestArea) { image = candidate; largestArea = area; }
    }
    return image;
  };
  const measure = (badge, parent, image, y) => {
    const parentRect = boundsOf(parent);
    const badgeRect = boundsOf(badge);
    const height = badgeRect.height || 24;
    const isCard = parent.matches('product-card');
    const rawTop = Math.max(0, Math.min(y ?? image.top + 4, image.bottom - height - 4) - parentRect.top);
    const top = isCard ? clamp(rawTop, 4, Math.max(4, parentRect.height - height - 4)) : rawTop;
    const rawRight = parentRect.right - image.right + 4;
    const right = isCard
      ? clamp(rawRight, 4, Math.max(4, parentRect.width - (badgeRect.width || 24) - 4))
      : Math.max(4, rawRight);
    const nextTop = Math.round(top * 100) / 100 + 'px';
    const nextRight = Math.round(right * 100) / 100 + 'px';
    return {badge, nextTop, nextRight};
  };
  const place = ({badge, nextTop, nextRight}) => {
    if (badge.style.top !== nextTop) badge.style.setProperty('top', nextTop);
    if (badge.style.right !== nextRight) badge.style.setProperty('right', nextRight);
    if (badge.dataset.languagePositioned !== 'true') badge.dataset.languagePositioned = 'true';
  };
  const schedule = (card = null) => {
    if (card) dirtySections.add(sectionOf(card));
    else allSectionsDirty = true;
    if (frame) return;
    // Badge coordinates are decorative: retain their CSS position for first paint
    // and measure after the browser has laid out the visible product images.
    const run = () => { frame = 0; layout(); };
    frame = 'requestIdleCallback' in window
      ? window.requestIdleCallback(run, {timeout: 200})
      : window.setTimeout(run, 50);
  };
  const imageSizes = new WeakMap();
  const visibleImages = new WeakMap();
  const resize = new ResizeObserver(entries => {
    for (const {target, contentRect} of entries) {
      const previous = imageSizes.get(target);
      imageSizes.set(target, {width: contentRect.width, height: contentRect.height});
      if (!previous || (previous.width === contentRect.width && previous.height === contentRect.height)) continue;
      const card = target.closest('product-card');
      if (activeCards.has(card)) schedule(card);
      else if (target.closest('.product-media[data-mox-detail-kind]')) schedule();
    }
  });
  const observe = image => {
    if (observed.has(image)) return;
    observed.add(image);
    resize.observe(image);
  };
  const activeCards = new Set();
  const watchedCards = new WeakSet();
  const visibility = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const wasActive = activeCards.has(entry.target);
      if (entry.isIntersecting) activeCards.add(entry.target);
      else activeCards.delete(entry.target);
      if (wasActive !== entry.isIntersecting) schedule(entry.target);
    }
  }, {rootMargin:'200px 0px'});
  const register = root => {
    const cards = root.matches('product-card') ? [root] : root.querySelectorAll('product-card');
    for (const card of cards) {
      const badge = card.querySelector('.mox-language-badge:not([data-language-detail])');
      if (badge && badge.parentElement !== card) card.append(badge);
      if (!badge) continue;
      if (!watchedCards.has(card)) {
        watchedCards.add(card);
        visibility.observe(card);
      }
      if (activeCards.has(card)) schedule(card);
    }
  };
  const layout = () => {
    const updateAll = allSectionsDirty;
    const changedSections = new Set(dirtySections);
    allSectionsDirty = false;
    dirtySections.clear();
    measuredBounds = new WeakMap();
    const placements = [];
    const sections = new Map();
    for (const card of activeCards) {
      if (!card.isConnected) {activeCards.delete(card);visibility.unobserve(card);continue;}
      const section = sectionOf(card);
      if (!updateAll && !changedSections.has(section)) continue;
      const badge = card.querySelector('.mox-language-badge:not([data-language-detail])');
      if (!badge) continue;
      const image = visibleCardImage(card);
      if (!image || !boundsOf(card).width) continue;
      visibleImages.set(card, image);
      observe(image);
      if (!sections.has(section)) sections.set(section, []);
      const rows = sections.get(section);
      const top = boundsOf(card).top;
      let row = rows.find(item => Math.abs(item.top - top) < 12);
      if (!row) {row = {top, items:[]}; rows.push(row);}
      row.items.push({card, badge, image:paintedBounds(image)});
    }
    sections.forEach(rows => rows.forEach(row => {
      const alignedY = Math.max(...row.items.map(item => item.image.top)) + 4;
      row.items.forEach(item => placements.push(measure(item.badge, item.card, item.image, alignedY)));
    }));
    if (updateAll) document.querySelectorAll('#MainContent .mox-language-badge[data-language-detail]').forEach(badge => {
      if (badge.closest('.dialog-zoomed-gallery')) return;
      const parent = badge.parentElement;
      const image = parent.querySelector(':scope > .product-media__image');
      if (!image || !boundsOf(image).width) return;
      observe(image);
      placements.push(measure(badge, parent, paintedBounds(image)));
    });
    measuredBounds = null;
    placements.forEach(place);
  };
  const start = () => {
    const main = document.querySelector('#MainContent');
    if (!main) return;
    register(main);
    schedule();
    main.addEventListener('load', event => {
      const image = event.target;
      if (!image.matches?.('img.product-media__image')) return;
      const card = image.closest('product-card');
      if (activeCards.has(card) && (!visibleImages.has(card) || visibleImages.get(card) === image)) schedule(card);
      else if (image.closest('.product-media[data-mox-detail-kind]')) schedule();
    }, true);
    main.addEventListener('scroll', event => {
      if (!event.target.matches?.('slideshow-slides')) return;
      const card = event.target.closest('product-card');
      if (activeCards.has(card)) schedule(card);
    }, {capture:true, passive:true});
    window.addEventListener('resize', () => schedule(), {passive:true});
    document.addEventListener('shopify:section:load', event => {
      if (event.target instanceof Element && main.contains(event.target)) register(event.target);
      schedule();
    });
    new MutationObserver(records => {
      const roots = new Set();
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (!node.matches('product-card,.mox-language-badge,.product-media,img.product-media__image') && !node.querySelector('product-card,.mox-language-badge,.product-media,img.product-media__image')) continue;
          roots.add(node.closest('product-card') || node);
          if (node.matches('.mox-language-badge[data-language-detail],.product-media[data-mox-detail-kind]') || node.closest('.product-media[data-mox-detail-kind]') || node.querySelector('.mox-language-badge[data-language-detail]')) schedule();
        }
      }
      // A fragment update only needs to register cards in that fragment.
      // Keep full-row alignment by measuring the affected section together.
      for (const root of roots) if (root.isConnected && main.contains(root)) register(root);
    }).observe(main, {childList:true,subtree:true});
    if (document.fonts?.ready) document.fonts.ready.then(() => schedule());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true}); else start();
})();

